import React, { CSSProperties, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Button, Container } from "@amzn/awsui-components-react";
import { PropertyFilterInput } from "./PropertyFilterInput";
import { RootState } from "../app/rootReducer";
import {
  PropertyFilter,
  PropertyFilterGroup,
  clearAppliedQuery,
  loadFromUrl,
  setActivePropertyFiltersVisibilityEnabled,
  setQueryMode,
  setQueryString,
} from "../searchSideBar/searchSideBarConnector";
import { getFilterWithAggregatedData } from "../searchSideBar/filterWithAggregatedDataConnector";
import { getProgramNames } from "../searchSideBar/upstreamAttributesConnector";
import { getProductLines } from "../searchSideBar/productLinesConnector";
import { setAsinPageNumber } from "../porAsinPageControl/porAsinPageControl";
import { setValidationPageNumber } from "../validation/asinValidationTableConnector";
import { setBaselinePageNumber } from "../dateBaseline/baselineTableConnector";
import { trackButtonClick } from "../telemetry/telemetry";

// Input grows to fill the row so the property filter uses the full
// canvas; Submit sits at the row's right end. `minWidth: 0` lets the
// polaris chip row wrap instead of overflowing.
const ROW_STYLE: CSSProperties = {
  display: "flex",
  gap: "8px",
  alignItems: "flex-start",
  flexWrap: "wrap",
};
const INPUT_STYLE: CSSProperties = { flex: 1, minWidth: 0 };

/** Disable Submit on empty state so we don't fire an `(all)` query. */
function hasAnyFilter(g: PropertyFilterGroup | undefined): boolean {
  if (!g || !g.filters || g.filters.length === 0) return false;
  return g.filters.some((f: PropertyFilter | PropertyFilterGroup) => {
    if (f.type === "PropertyFilter") {
      if (typeof f.value === "string") return f.value.trim().length > 0;
      return f.value !== undefined && f.value !== null;
    }
    return !!f.filters && f.filters.length > 0;
  });
}

/**
 * App Shell Filter Bar. On mount dispatches the same data loads as
 * Legacy `SearchSideBar` so dropdowns populate, and forces
 * `queryMode = "PropertyFilter"` so Submit reads the right slice
 * field. Submit resets per-view page counters (LEAP-7748).
 */
export const FilterBar: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { queryMode, pendingQueryString, queryPropertyFilters } = useSelector(
    (state: RootState) => state.generateQuery
  );
  const { isEditing } = useSelector(
    (state: RootState) => state.asinBaselineTableControl
  );
  const { filterWithAggregatedData } = useSelector(
    (state: RootState) => state.filterWithAggregatedData
  );

  const canSubmit = useMemo(
    () => hasAnyFilter(queryPropertyFilters),
    [queryPropertyFilters]
  );

  // On nav, re-sync chips with the URL hash. Skip when no hash so
  // we don't clobber a cleared query with `(all)`.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash) {
      dispatch(loadFromUrl());
    }
  }, [location.pathname]);

  // Guard: PorAsinListTable's `loadFromUrl()` can flip mode to
  // `UIFilter`, silently breaking Submit. Snap back.
  useEffect(() => {
    if (queryMode !== "PropertyFilter") {
      dispatch(setQueryMode("PropertyFilter"));
    }
  }, [queryMode]);

  useEffect(() => {
    dispatch(getFilterWithAggregatedData());
    dispatch(setActivePropertyFiltersVisibilityEnabled(true));
  }, []);

  useEffect(() => {
    if (filterWithAggregatedData?.status === "finished") {
      dispatch(getProgramNames());
      dispatch(getProductLines());
    }
  }, [filterWithAggregatedData]);

  // On Clear filters (canSubmit true -> false): wipe queryString +
  // URL hash so tables skip fetching until the next Submit.
  const prevCanSubmitRef = useRef(canSubmit);
  useEffect(() => {
    if (prevCanSubmitRef.current && !canSubmit) {
      dispatch(clearAppliedQuery());
    }
    prevCanSubmitRef.current = canSubmit;
  }, [canSubmit]);

  const onSubmit = () => {
    trackButtonClick("filterBar", "Submit", {
      queryMode,
      query: pendingQueryString,
    });
    // Guarantee PropertyFilter mode in case of route-change drift.
    dispatch(setQueryMode("PropertyFilter"));
    dispatch(setQueryString());
    // LEAP-7748: reset page numbers on fresh search.
    dispatch(setAsinPageNumber({ pageNumber: 1 }));
    dispatch(setValidationPageNumber({ pageNumber: 1 }));
    dispatch(setBaselinePageNumber({ pageNumber: 1 }));
  };

  return (
    <Container>
      <div data-testid="filter-bar" style={ROW_STYLE}>
        <div style={INPUT_STYLE}>
          <PropertyFilterInput enableFreeTextAsin />
        </div>
        <Button
          data-testid="filter-bar-submit-button"
          variant="primary"
          disabled={isEditing || !canSubmit}
          onClick={onSubmit}
        >
          Submit
        </Button>
      </div>
    </Container>
  );
};

export default FilterBar;
